import { clsx } from "clsx";

import { Badge } from "components/ui/badge";
import {
  Combobox,
  ComboboxChip,
  ComboboxChips,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxValue,
} from "components/ui/combobox";
import { MultipleFilterTrigger } from "components/NewAdvancedSearch/filters/MultipleFilterTrigger";
import { OperatorSelect } from "components/NewAdvancedSearch/filters/OperatorSelect";
import { useStickyChipScroll } from "components/NewAdvancedSearch/filters/useStickyChipScroll";
import { getTextColor } from "helpers/tags";

const OPERATIONS = [
  { symbol: "∋", text: "Contains", value: "contains" },
  { symbol: "∌", text: "Excludes", value: "excludes" },
];
const initialValue = {
  op: "contains",
  value: [],
};

const defaultTagBg = "#fff";
const defaultTagText = "rgba(143, 143, 143, 1)";

export function TagFilter({ descriptor, value, onChange }) {
  const current = value ?? initialValue;
  const options = descriptor.data ?? [];
  const labelFor = id => options.find(o => o.id === id)?.name ?? id;
  const { ref: chipsRef, onScroll: onChipsScroll } = useStickyChipScroll(
    (current.value ?? []).length
  );

  const handleChangeOp = op =>
    onChange({
      op,
      value: current.value,
    });

  const handleChangeValue = next =>
    onChange({
      value: next,
      op: current.op,
    });

  const getTagStyle = id => {
    const tag = options.find(op => op.id === id);
    if (!tag?.enabled) {
      return { backgroundColor: defaultTagBg, color: defaultTagText };
    }
    return {
      backgroundColor: tag.color,
      color: getTextColor(tag.color),
    };
  };

  return (
    <div className="tw:flex tw:min-w-0 tw:flex-1 tw:items-center tw:gap-2">
      <OperatorSelect
        operations={OPERATIONS}
        value={current.op}
        onChange={handleChangeOp}
      />
      <Combobox
        items={options}
        multiple
        value={current.value ?? []}
        onValueChange={handleChangeValue}
        itemToStringLabel={o => o.name}
      >
        <MultipleFilterTrigger
          getTagStyle={getTagStyle}
          labelFor={labelFor}
          keyFor={id => id}
          placeholder="Select Tags"
        />
        <ComboboxContent>
          <ComboboxChips
            render={<div ref={chipsRef} onScroll={onChipsScroll} />}
            className={clsx(
              !current?.value?.length && "tw:min-h-0 tw:p-0",
              "tw:max-h-44 tw:overflow-y-auto"
            )}
          >
            <ComboboxValue>
              {selected =>
                selected.map(id => (
                  <ComboboxChip
                    key={id}
                    style={getTagStyle(id)}
                    className="tw:h-auto tw:max-w-full tw:min-w-0 tw:justify-start tw:text-left tw:whitespace-normal"
                  >
                    <span className="tw:min-w-0 tw:wrap-anywhere">
                      {labelFor(id)}
                    </span>
                  </ComboboxChip>
                ))
              }
            </ComboboxValue>
          </ComboboxChips>
          <ComboboxInput showTrigger={false} placeholder="Search" />
          <ComboboxEmpty>No options found.</ComboboxEmpty>
          <ComboboxList>
            {item => (
              <ComboboxItem key={item.id} value={item.id}>
                <Badge
                  style={getTagStyle(item.id)}
                  className="tw:h-auto tw:max-w-full tw:min-w-0 tw:shrink tw:justify-start tw:text-left tw:whitespace-normal tw:wrap-anywhere"
                >
                  {item.name}
                </Badge>
              </ComboboxItem>
            )}
          </ComboboxList>
        </ComboboxContent>
      </Combobox>
    </div>
  );
}
